import { useTranslation } from '@pancakeswap/localization'
import styled from 'styled-components'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Button,
  Card,
  CardBody,
  PlayCircleOutlineIcon,
  Text,
  useTooltip,
} from '@pancakeswap/uikit'
import { useWeb3React } from '@pancakeswap/wagmi'
import { ToastDescriptionWithTx } from 'components/Toast'
import useLocalDispatch from 'contexts/LocalRedux/useLocalDispatch'
import useTheme from 'hooks/useTheme'
import { useEffect, useMemo, useState } from 'react'
import { fetchLedgerData } from 'state/predictions'
import { ROUND_BUFFER } from 'state/predictions/config'
import { BetPosition, NodeLedger, NodeRound } from 'state/types'
import { getNow } from 'utils/getNow'
import { useConfig } from 'views/Predictions/context/ConfigProvider'
import { helperToast } from 'utils/helperToast'
import { formatBnbv2 } from '../../helpers'
import CardFlip from '../CardFlip'
import { PrizePoolRow, RoundResultBox } from '../RoundResult'
import CardHeader, { getBorderBackground } from './CardHeader'
import MultiplierArrow from './MultiplierArrow'
import SetPositionCard from './SetPositionCard'

// const StyledEnterButton = styled(Button)`
//   font-size: 14px;
//   color: ${(props) => props.textColor || 'white'};
// `

// const StyledDisableEnteredButton = styled(Button)`
//   height: 36px;
//   font-size: 14px;
//   font-weight: 700;
// `

const StyledRoundResultBox = styled(RoundResultBox)`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

interface OpenRoundCardProps {
  round: NodeRound
  betAmount?: NodeLedger['amount']
  hasEnteredUp: boolean
  hasEnteredDown: boolean
  bullMultiplier: string
  bearMultiplier: string
}

interface State {
  isSettingPosition: boolean
  position: BetPosition
}

const OpenRoundCard: React.FC<React.PropsWithChildren<OpenRoundCardProps>> = ({
  round,
  betAmount,
  hasEnteredUp,
  hasEnteredDown,
  bullMultiplier,
  bearMultiplier,
}) => {
  const [state, setState] = useState<State>({
    isSettingPosition: false,
    position: BetPosition.BULL,
  })
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { account } = useWeb3React()
  const dispatch = useLocalDispatch()
  const { token, displayedDecimals } = useConfig()
  const { lockTimestamp } = round ?? { lockTimestamp: null }
  const { isSettingPosition, position } = state
  const [isBufferPhase, setIsBufferPhase] = useState(false)
  const positionDisplay = useMemo(
    () => (position === BetPosition.BULL ? t('Up').toUpperCase() : t('Down').toUpperCase()),
    [t, position],
  )
  const positionEnteredText = useMemo(
    () => (hasEnteredUp ? t('Up').toUpperCase() : hasEnteredDown ? t('Down').toUpperCase() : null),
    [t, hasEnteredUp, hasEnteredDown],
  )
  const positionEnteredIcon = useMemo(
    () =>
      hasEnteredUp ? (
        <ArrowUpIcon color="currentColor" />
      ) : hasEnteredDown ? (
        <ArrowDownIcon color="currentColor" />
      ) : null,
    [hasEnteredUp, hasEnteredDown],
  )
  const { targetRef, tooltipVisible, tooltip } = useTooltip(
    <div style={{ whiteSpace: 'nowrap' }}>{`${formatBnbv2(betAmount, displayedDecimals)} FTM`}</div>,
    { placement: 'top' },
  )

  useEffect(() => {
    const secondsToLock = lockTimestamp ? lockTimestamp - getNow() : 0
    if (secondsToLock > 0) {
      const setIsBufferPhaseTimeout = setTimeout(() => {
        setIsBufferPhase(true)
      }, (secondsToLock - ROUND_BUFFER) * 1000)

      return () => {
        clearTimeout(setIsBufferPhaseTimeout)
      }
    }
    return undefined
  }, [lockTimestamp])

  const getHasEnteredPosition = () => {
    if (hasEnteredUp || hasEnteredDown) {
      return false
    }

    if (round.lockPrice !== null) {
      return false
    }

    return true
  }

  const canEnterPosition = getHasEnteredPosition()

  const handleBack = () =>
    setState((prevState) => ({
      ...prevState,
      isSettingPosition: false,
    }))

  const handleSetPosition = (newPosition: BetPosition) => {
    setState((prevState) => ({
      ...prevState,
      isSettingPosition: true,
      position: newPosition,
    }))
  }

  const togglePosition = () => {
    setState((prevState) => ({
      ...prevState,
      position: prevState.position === BetPosition.BULL ? BetPosition.BEAR : BetPosition.BULL,
    }))
  }

  const handleSuccess = async (hash: string) => {
    await dispatch(fetchLedgerData({ account, epochs: [round.epoch] }))

    handleBack()
    helperToast.success(
      <ToastDescriptionWithTx txHash={hash}>
        {t('%position% position entered', {
          position: positionDisplay,
        })}
      </ToastDescriptionWithTx>,
    )
  }

  return (
    <CardFlip isFlipped={isSettingPosition} height="500px">
      <Card borderBackground={getBorderBackground(theme, 'next')}>
        <CardHeader
          status="next"
          epoch={round.epoch}
          icon={<PlayCircleOutlineIcon color="textMain" mr="4px" width="24px" />}
          title={t('Next')}
        />
        <CardBody p="16px">
          <MultiplierArrow betAmount={betAmount} multiplier={bullMultiplier} hasEntered={hasEnteredUp} />
          <StyledRoundResultBox isNext isLive={!canEnterPosition}>
            {canEnterPosition ? (
              <>
                <PrizePoolRow totalAmount={round.totalAmount} mb="16px" />
                {!canEnterPosition || isBufferPhase ? (
                  <Button
                    variant="success"
                    width="100%"
                    onClick={() => handleSetPosition(BetPosition.BULL)}
                    mb="16px"
                    height={36}
                    disabled
                  >
                    <Text fontSize="14px" fontWeight={700} color="textPrimary">
                      {t('Enter UP')}
                    </Text>
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    width="100%"
                    onClick={() => handleSetPosition(BetPosition.BULL)}
                    mb="16px"
                    height={36}
                    disabled={!canEnterPosition || isBufferPhase}
                  >
                    <Text fontSize="14px" fontWeight={700} color="textPrimary">
                      {t('Enter UP')}
                    </Text>
                  </Button>
                )}

                <Button
                  variant="danger"
                  width="100%"
                  height={36}
                  onClick={() => handleSetPosition(BetPosition.BEAR)}
                  disabled={!canEnterPosition || isBufferPhase}
                >
                  <Text fontSize="14px" fontWeight={700} color="textPrimary">
                    {t('Enter DOWN')}
                  </Text>
                </Button>
              </>
            ) : positionEnteredText ? (
              <>
                <div ref={targetRef}>
                  <Button disabled startIcon={positionEnteredIcon} width="100%" mb="16px">
                    <Text fontWeight={700} color="textPrimary">
                      {t('%position% Entered', { position: positionEnteredText })}
                    </Text>
                  </Button>
                </div>
                <PrizePoolRow totalAmount={round.totalAmount} />
                {tooltipVisible && tooltip}
              </>
            ) : (
              <>
                <div>
                  <Button disabled width="100%" mb="16px">
                    <Text fontWeight={700} color="textPrimary">
                      {t('No position entered')}
                    </Text>
                  </Button>
                </div>
                <PrizePoolRow totalAmount={round.totalAmount} />
              </>
            )}
          </StyledRoundResultBox>
          <MultiplierArrow
            betAmount={betAmount}
            multiplier={bearMultiplier}
            betPosition={BetPosition.BEAR}
            hasEntered={hasEnteredDown}
          />
        </CardBody>
      </Card>
      <SetPositionCard
        onBack={handleBack}
        onSuccess={handleSuccess}
        position={position}
        togglePosition={togglePosition}
        epoch={round.epoch}
      />
    </CardFlip>
  )
}

export default OpenRoundCard
